// Acrobat JS runtime constraints — notes for future editors of this file.
//
// 1. Cannot set field.value during a field's own Calculate event.
//    Acrobat throws InvalidSetError. Calling setItems() on a combobox resets
//    its .value to getItemAt(0) and synchronously fires that field's Calculate
//    event. If cfgTool_disableOptions is running inside that Calculate handler
//    and tries to call setItems() again on the triggering field, Acrobat
//    hard-prohibits it. Workaround: the triggering_field parameter is threaded
//    through to cfgTool_disableOptions, which skips setItems entirely for that
//    field. The user's selection is already correct; the trimmed option list
//    is applied on the next reprocessLogic triggered by a different field.
//
// 2. setItems() fires Calculate synchronously on the target field.
//    If the field's current selection is still valid in the new option list,
//    calling setItems() anyway resets .value to the placeholder and immediately
//    re-enters cfgTool_fieldChanged, corrupting prev_vars and cascading into
//    unrelated field resets. Workaround: only call setItems when the current
//    pick is itself disabled. When the current pick is still valid, defer the
//    option-list trim to the next reprocessLogic from a different field.
//
// 3. Stale product IDs in a3 additional-options.
//    The a3 additional-options filter junction references product IDs (18, 22,
//    11, 28, 5, 25) that no longer exist in the products collection. Null-guards
//    are in place at cfgTool_disableOptions (!field, !product_entry), resetField,
//    and disableAllFields. If those guards are removed or new orphaned IDs
//    accumulate, the Calculate handler will crash on null.type. Keep the guards;
//    flag any new orphaned IDs in this comment and add corresponding null-checks.
//
// 4. Host calls are the performance budget (selection-hang fix, 2026-06).
//    A single selection used to make hundreds of getField/property host calls
//    inside the Calculate event, freezing the Acrobat UI for seconds. Three
//    mechanisms keep that down — preserve them when editing:
//    - cfgTool_field(): caches Field handles (the form is static; null misses
//      are cached too). Use it instead of getField() in this file.
//    - cfgTool_getSelectedIdsMap(): one selection snapshot per matcher run;
//      logic_cfg clauses are evaluated as map lookups, never per-id getField.
//    - cfgTool_cell_state / cfgTool_row_state: skip rewriting table cells and
//      re-toggling rows whose content/visibility didn't change. If any other
//      code starts writing those fields/layers, these caches go stale.
//    - cfgTool_disableAllFields only writes field.readonly when it changes.
//
// 5. Config-image rendering is the dominant cost, so image and dimensions live
//    on SEPARATE layers (generator getContentElements):
//    - image-${i}: one OCG layer per DISTINCT image (deduped). setting_image_map
//      maps a matched SETTING -> its image layer. cfgTool_showImageFromLogic
//      leaves these layers untouched (cfgTool_shown_image_layer cache) when a
//      selection keeps the same image, so the product raster isn't re-rendered
//      just because options/dimensions changed.
//    - dims-${settingIdx}: the per-setting width/height/depth/weight readout
//      (cheap text, separate page region), toggled per matched setting via
//      cfgTool_shown_dims_layer.
//    Without setting_image_map (older PDFs) the image path falls back to one
//    layer per setting and dims-* toggles are harmless no-ops. cfgTool_match_idx
//    stays the SETTING index.

const scripts = `

try {
  // Expected to be in the following form:
  // {
    // 'module-name': {
      // product_names_sorted,
      // dropdown_products,
      // checkbox_products,
      // product_names_map,
      // all_product_names_by_sku_map,
      // related_models_map,
      // product_ids_name_map,
      // logic_field_names,
      // logic_cfg,
      // logic_fields_map,
      // project,
      // same_dimensions
      // logic_field_names_v2
      // max_products
    // }
  // }
  var cfgTool_vars = {{script_vars}};
  // The Doc. Captured here at top level, where 'this' is the document (inside
  // the bare-called cfgTool_* functions 'this' is the global, not the Doc).
  // Used to batch field/layer redraws via Doc.delay — see cfgTool_fieldChanged.
  var cfgTool_doc = this;
  // Re-entrancy depth for cfgTool_fieldChanged: setting a field's value below
  // can synchronously fire that field's Calculate -> cfgTool_fieldChanged again.
  // Only the OUTERMOST call manages Doc.delay so the batch isn't released early.
  var cfgTool_fc_depth = 0;
  //{module-name:[]}
  var cfgTool_disabled_state = {};
  var cfgTool_deselected_state = {};
  //{module-name:[]}
  var cfgTool_dropdown_options_state = {};
  //keep track of the idx of the matched setting
  //{module-name:-1}
  var cfgTool_match_idx = {};
  // Previous variables by module name
  // e.g. { 'module-1_dropdown-1' : '2-4 Hole Punch Kit', ... }
  var cfgTool_prev_vars = {};

  // ---- Host-call caches (perf) ----
  // Every Acrobat host call (getField lookup, field property write) is
  // expensive; one interaction was making hundreds of them inside the
  // Calculate event, freezing the UI until the chain finished. The form is
  // static after generation, so field handles (including null misses from the
  // enumeration probes) are cached for the document's lifetime.
  var cfgTool_field_cache = {};
  // Last content written per table cell ("<text>|<bold>"); rich/multiline
  // setup is done once on first write. Only cfgTool_setCellValue may write
  // these fields, or the cache goes stale.
  var cfgTool_cell_state = {};
  // Last visibility applied per table row; only cfgTool_toggleRow may toggle
  // row layers/cells, or the cache goes stale.
  var cfgTool_row_state = {};
  // Currently-shown config-image layer index per module. Toggling an image
  // layer forces Acrobat to recomposite the whole (image-heavy) page, so when
  // the matched setting maps to the layer already shown we skip the toggle.
  var cfgTool_shown_image_layer = {};
  // Currently-shown dimensions layer (per setting) per module.
  var cfgTool_shown_dims_layer = {};

  function cfgTool_field(field_name) {
    if (field_name in cfgTool_field_cache) {
      return cfgTool_field_cache[field_name];
    }
    var field = getField(field_name);
    cfgTool_field_cache[field_name] = field;
    return field;
  }


  function cfgTool_enumerateFieldsByPrefix(prefix) {
    var fields = [];
    var idx = 0;
    var found;
    do {
      // Build name: <pfx><idx>
      var field_name = prefix + idx;
      var field = cfgTool_field(field_name);
      found = !!field;
      if (found) {
        fields.push(field);
      }
      ++idx;
    } while (found);
    return fields;
  }
  
  function cfgTool_initCheckboxListener(module_name_pfx, field_name) {
    var field = cfgTool_field(field_name);
    if (!field) return false;
    field.setAction(
      "MouseUp",
      "cfgTool_fieldChanged('" + module_name_pfx + "', '" + field_name + "')"
    );
  
    // Cache current value
    cfgTool_prev_vars[field_name] = field.value;
  
    return true;
  }
  
  function cfgTool_enumerateCheckboxes(module_name_pfx) {
    var cbox_container_idx = 0;
    var checkboxes_found;
    var found_items = [];
    do {
      checkboxes_found = false;
      // Build name: <pfx>_checkboxes-<container_idx>- (without the last idx, for later)
      var field_name_base =
        module_name_pfx + "_content_checkboxes-" + cbox_container_idx + "-";
  
      const checkboxes = cfgTool_enumerateFieldsByPrefix(field_name_base);
      if (checkboxes.length > 0) checkboxes_found = true;
  
      found_items = found_items.concat(checkboxes);
  
      ++cbox_container_idx;
    } while (checkboxes_found);
    return found_items;
  }
  
  // Checkboxes are named as follows:
  // <module_name_pfx>_checkboxes-<cbox_container_idx>-<cbox_idx>
  function cfgTool_initCheckboxListeners(module_name_pfx) {
    const checkboxes = cfgTool_enumerateCheckboxes(module_name_pfx);
    checkboxes.forEach((f) => {
      cfgTool_initCheckboxListener(module_name_pfx, f.name);
    });
  }
  
  function cfgTool_initDropdownListener(module_name_pfx, field_name) {
    var field = cfgTool_field(field_name);
    if (!field) return false;
  
    field.commitOnSelChange = true;
    field.setAction(
      "Calculate",
      "cfgTool_fieldChanged('" + module_name_pfx + "', '" + field_name + "')"
    );
  
    // Cache current value
    cfgTool_prev_vars[field_name] = field.value;
  
    const options = [];
    for (var i = 0; i < field.numItems; i++) {
      const optionValue = field.getItemAt(i);
      const optionLabel = field.getItemAt(i, false);
      options.push([optionLabel, optionValue]);
    }
    // store original options of the the dropdown
    cfgTool_dropdown_options_state[module_name_pfx][field_name] = options;
    return true;
  }
  
  function cfgTool_enumerateDropdowns(module_name_pfx) {
    return cfgTool_enumerateFieldsByPrefix(
      module_name_pfx + "_content_dropdown-"
    );
  }
  
  // Dropdowns are named as follows:
  // <module_name_pfx>_dropdown-<dropdown_idx>
  function cfgTool_initDropdownListeners(module_name_pfx) {
    const dropdown_fields = cfgTool_enumerateDropdowns(module_name_pfx);
    dropdown_fields.forEach((f) => {
      cfgTool_initDropdownListener(module_name_pfx, f.name);
    });
  }

  //for product's x skus, add product x times with the corresponding sku
  //only applicable for am-a3 and am-a4 (set on frontend)
  function cfgTool_addProductToSelectedProducts(module_name_pfx, selected_products, product) {
    if (!product) return;
    const productName = product.model;
    // AM projects: rows are precomputed on the build side to match the configurator exactly
    if (product.rows && product.rows.length) {
      for (var r = 0; r < product.rows.length; r++) {
        selected_products.push({
          model: product.rows[r].name,
          id: product.id,
          sku: product.rows[r].sku,
        });
      }
      return;
    }
    const { all_product_names_by_sku_map } = cfgTool_vars[module_name_pfx];
    for (var i = 0; i < product.skus.length; i++) {
      const skuProductName = all_product_names_by_sku_map[product.skus[i]] || productName;
      selected_products.push({
        model: skuProductName,
        id: product.id,
        sku: product.skus[i],
      });
    }
  }

  function cfgTool_getSortedSelectedProducts(module_name_pfx) {
    var selected_products = [];
    const dropdowns = cfgTool_enumerateDropdowns(module_name_pfx);
    const checkboxes = cfgTool_enumerateCheckboxes(module_name_pfx);
    const cfgTool_fields = [...dropdowns, ...checkboxes];
  
    const { related_models_map, product_names_map, product_ids_name_map } =
      cfgTool_vars[module_name_pfx];
  
    cfgTool_fields.forEach((f) => {
      const selected_product = cfgTool_getProduct(module_name_pfx, f);
      if (selected_product) {
        cfgTool_addProductToSelectedProducts(module_name_pfx, selected_products, selected_product);
  
        // Also include related models
        const related_models = related_models_map[selected_product.id];
        if (related_models && related_models.length > 0) {
          related_models.forEach((id) => {
            const related_model_name = product_ids_name_map[id];
            const related_model = product_names_map[related_model_name];
            cfgTool_addProductToSelectedProducts(module_name_pfx, selected_products, related_model);
          });
        }
      }
    });
  
    return selected_products;
  }
  
  // Update overline, title and part numbers
  function cfgTool_updateFieldValues(module_name_pfx) {
    // Skip overline since it is hardcoded to read "WorkForce Enterprise"
    var primary_dropdown = cfgTool_field(module_name_pfx + "_content_dropdown-0");
    var primary_product = cfgTool_getProduct(module_name_pfx, primary_dropdown);
    if (primary_product) {
      const { dropdown_products } = cfgTool_vars[module_name_pfx] || {};
      const first_key = Object.keys(dropdown_products)[0];
      const products = dropdown_products[first_key];
      products.forEach((id) => {
        const show_layer = id === primary_product.id;
        toggleLayer(module_name_pfx + "_content_title-" + id, show_layer);
      });
    }
  
    // Keep track of row index to update
    // Show table values: dropdown values followed by checkbox ones, in order.
    const sorted_selected_products =
      cfgTool_getSortedSelectedProducts(module_name_pfx);
    var row_idx = sorted_selected_products.length;

    cfgTool_toggleTableValues(module_name_pfx, sorted_selected_products, true);

    const max_products = cfgTool_getMaxProducts(module_name_pfx);

    // Hide remaining rows
    for (row_idx; row_idx < max_products; ++row_idx) {
      cfgTool_toggleRow(module_name_pfx, row_idx, false);
    }
  }

  function cfgTool_setCellValue(field_name, value, is_bold) {
    const value_str = value || "";
    const cell_key = value_str + "|" + (is_bold ? 1 : 0);
    const prev_key = cfgTool_cell_state[field_name];
    // Rewriting a rich-text cell regenerates its appearance — one of the most
    // expensive Acrobat field operations — so skip cells that haven't changed.
    if (prev_key === cell_key) return;
    var field = cfgTool_field(field_name);
    if (!field) return;
    if (prev_key === undefined) {
      // One-time setup: re-assigning these on every write forced Acrobat to
      // redo appearance work even when nothing changed.
      // Always treat as multiline.
      field.multiline = true;
      field.richText = true;
    }
    cfgTool_cell_state[field_name] = cell_key;

    field.richValue = [
      {
        fontWeight: is_bold ? 900 : 400,
        fontFamily: ["Helvetica"],
        text: value_str,
      },
    ];
  }
  function cfgTool_setRowValues(module_name_pfx, row_idx, v_th, v_td) {
    const th_name = module_name_pfx + "_content_th-" + row_idx;
    // setFieldValue(th_name, v_th);
    cfgTool_setCellValue(th_name, v_th, true);
    const td_name = module_name_pfx + "_content_td-" + row_idx;
    // setFieldValue(td_name, v_td);
    cfgTool_setCellValue(td_name, v_td);
  }
  
  // Also remove field values so that when the field is shown it is empty.
  // Otherwise, on slower systems we may sometimes briefly see the field with old content.
  function cfgTool_hideTableValues(module_name_pfx) {
    const empty_values = makeEmptyArray(cfgTool_getMaxProducts(module_name_pfx), {});
    cfgTool_toggleTableValues(module_name_pfx, empty_values, false);
  }
  // Toggle visibility
  function cfgTool_toggleTableValues(module_name_pfx, table_values_list, state) {
    const max_table_elements = cfgTool_getMaxProducts(module_name_pfx);
    table_values_list.forEach((obj, row_idx) => {
      // TODO: should limit be 15 or based on products?
      if (row_idx >= max_table_elements) return;
      const { model, sku } = obj || {};
      const v_th = (state && model) || "";
      const v_td = (state && sku) || "";
      cfgTool_setRowValues(module_name_pfx, row_idx, v_th, v_td);
      cfgTool_toggleRow(module_name_pfx, row_idx, state);
    });
  }
  
  function cfgTool_toggleRow(module_name_pfx, row_idx, state) {
    const row_key = module_name_pfx + "_" + row_idx;
    // Skip rows already in the requested state (each visibility write below
    // is several host calls; most interactions only change a few rows).
    if (cfgTool_row_state[row_key] === state) return;
    cfgTool_row_state[row_key] = state;
    // Hide table BG & border
    toggleLayer(module_name_pfx + "_content_row-" + row_idx, state);
    // Hide header field
    toggleFieldVisibility(module_name_pfx + "_content_th-" + row_idx, state);
    // Hide value field
    toggleFieldVisibility(module_name_pfx + "_content_td-" + row_idx, state);
  }
  
  // Show the image + dimension layers for the matched setting.
  // Image and dimensions are on SEPARATE layers: the image layer is deduped
  // (one per distinct image) and left untouched when consecutive selections
  // share a silhouette, so Acrobat doesn't re-render the product image just
  // because options/dimensions changed. The dimensions layer is per-setting.
  function cfgTool_showImageFromLogic(module_name_pfx) {
    if (!cfgTool_vars[module_name_pfx]) return 0;

    const { logic_cfg, setting_image_map } = cfgTool_vars[module_name_pfx];

    // cfgTool_match_idx stays the matched SETTING index (other code/tests read
    // it as such). The image layer it maps to may be shared with other settings.
    const match = cfgTool_getImageIndexToShow(module_name_pfx);
    cfgTool_match_idx[module_name_pfx] = match;

    // --- Image layer (deduped) ---
    // Resolve the image LAYER for this setting. Fall back to identity (one layer
    // per setting) for PDFs generated before setting_image_map existed.
    var target_image = match;
    var image_layer_count = logic_cfg.length;
    if (setting_image_map) {
      target_image = match < 0 ? -1 : setting_image_map[match];
      if (target_image === undefined || target_image === null) target_image = -1;
      image_layer_count = 0;
      for (var s = 0; s < setting_image_map.length; ++s) {
        if (setting_image_map[s] + 1 > image_layer_count)
          image_layer_count = setting_image_map[s] + 1;
      }
    }
    // Only touch image layers when the target changes — switching between
    // settings that share an image leaves the image layer (and its render) be.
    if (cfgTool_shown_image_layer[module_name_pfx] !== target_image) {
      cfgTool_shown_image_layer[module_name_pfx] = target_image;
      for (var i = 0; i < image_layer_count; ++i) {
        toggleLayer(module_name_pfx + "_content_image-" + i, i === target_image);
      }
    }

    // --- Dimensions layer (per setting) ---
    // PDFs generated before the image/dims split have no dims-* layers; those
    // toggles are harmless no-ops (the layer simply doesn't exist).
    if (cfgTool_shown_dims_layer[module_name_pfx] !== match) {
      cfgTool_shown_dims_layer[module_name_pfx] = match;
      for (var d = 0; d < logic_cfg.length; ++d) {
        toggleLayer(module_name_pfx + "_content_dims-" + d, d === match);
      }
    }
  }
  
  // Returns whether a portion of configuration logic "matches"
  // a field's value.
  // config is expected to be a string such as "> 0", "= 0", "= 10"
  //   - A value of 0 would mean no product is selected for the field
  function cfgTool_fieldMatchesConfig(module_name_pfx, field, config) {
    const field_product = cfgTool_getProduct(module_name_pfx, field);
  
    // Here we expect the following format:
    //   <operator><space><value>
    // e.g. "> 10", "= 0"
    const [operator, comparing_value] = config.split(" ");
  
    // Make inexistent products have a value of 0
    if (!field_product) {
      return compareValues(0, comparing_value, operator);
    }
  
    const value = field_product.id;
    return compareValues(value, comparing_value, operator);
  }
  
  // Snapshot of the currently selected product ids across all config-tool
  // fields: { <product_id>: true }. Built once per matcher run so evaluating
  // logic_cfg is pure in-memory work — the previous per-id field re-querying
  // (reverse_products -> getField -> getProduct for every id of every clause
  // of every cfg) was the bulk of the host calls per interaction.
  function cfgTool_getSelectedIdsMap(module_name_pfx) {
    const selected = {};
    const { all_products } = cfgTool_vars[module_name_pfx];
    Object.keys(all_products).forEach((field_name) => {
      const product = cfgTool_getProduct(
        module_name_pfx,
        cfgTool_field(field_name)
      );
      if (product) selected[product.id] = true;
    });
    return selected;
  }

  // Return the matching index from logic
  // We expect logic to be in the following form from logic_cfg:
  //   { 0: "> 0", 1: "= 0", 2: "= 0", 3: "= 0" }
  // where each key maps to a field name by logic_fields_map
  // equivalent of finding matched setting on frontend
  function cfgTool_getImageIndexToShow(module_name_pfx) {
    if (!cfgTool_vars[module_name_pfx]) return 0;

    const { logic_cfg, logic_fields_map } = cfgTool_vars[module_name_pfx];
    // Built lazily on the first newLogic cfg; field values cannot change
    // while the matcher runs (it is read-only), so one snapshot is valid for
    // the whole scan.
    var selected_ids = null;

    for (var i = 0; i < logic_cfg.length; ++i) {
      const cfg = logic_cfg[i];
      // e.g. [0,1,2,3]
      const keys = Object.keys(cfg);
      const all_match = keys.every((key) => {
        if (key === "newLogic") return true;
        if (cfg.newLogic) {
          if (!selected_ids) {
            selected_ids = cfgTool_getSelectedIdsMap(module_name_pfx);
          }
          const op = key.split("_")[0];
          const idList = cfg[key];
          /**
           * disabled: just says which ids should be disabled; disregard here
           * deselected: just says which ids should be deselected; disregard here
           * any: true if any of the ids is currently selected
           * includes: true if every id is currently selected
           * excludes: true if none of the ids is currently selected
           * (an id whose field doesn't exist is simply never selected)
           */
          if (op === "disabled" || op === "deselected") return true;
          if (op === "any") {
            return idList.some((x_any) => !!selected_ids[x_any]);
          }
          return idList.every((x_includes) =>
            op === "includes" ? !!selected_ids[x_includes] : !selected_ids[x_includes]
          );
        } else {
          const field_name = logic_fields_map[key];
          const field = cfgTool_field(field_name);
          return cfgTool_fieldMatchesConfig(module_name_pfx, field, cfg[key]);
        }
      });
  
      if (all_match) {
        if (cfg.newLogic) {
          cfgTool_disabled_state[module_name_pfx] = cfg.disabled;
          cfgTool_deselected_state[module_name_pfx] = cfg.deselected;
        }
        return i;
      }
    }
    //set the disabled options associated with the matched setting
    cfgTool_disabled_state[module_name_pfx] = [];
    cfgTool_deselected_state[module_name_pfx] = [];
    return -1;
  }
  
  function cfgTool_getMaxProducts(module_name_pfx) {
    const { max_products } = cfgTool_vars[module_name_pfx];
    return max_products;
  }
  
  const title_field_names = ["_overline", "_title", "_email-btn"];
  // These are **parts** of the name. For example, <module>_th-<idx>
  const table_field_names = ["_th-", "_td-"];
  function cfgTool_toggleConfigSection(module_name_pfx, state) {
    const pfx = module_name_pfx + "_content";
    toggleLayer(pfx, state);
  
    // Export button visibility
    const export_visibility_lyr_name =
      module_name_pfx + "_helper_export-btn-visibility";
    toggleLayer(export_visibility_lyr_name, !state);
  
    // Toggle fields
    title_field_names.forEach((n) => {
      const field_name = pfx + n;
      toggleFieldVisibility(field_name, state);
    });
  
    // Hide table cells
    if (!state) {
      cfgTool_hideTableValues(module_name_pfx);
    }
  }
  
  function cfgTool_hideConfigSection(module_name_pfx) {
    cfgTool_toggleConfigSection(module_name_pfx, false);
  }
  
  function cfgTool_showConfigSection(module_name_pfx, id) {
    cfgTool_toggleConfigSection(module_name_pfx, true);
  }
  
  function cfgTool_fieldIsUsedInLogic(module_name_pfx, name) {
    if (!cfgTool_vars[module_name_pfx]) {
      console.println(
        'ERROR: Module config for "' + module_name_pfx + '" not found'
      );
      return null;
    }
    return cfgTool_vars[module_name_pfx].logic_field_names[name];
  }
  
  // New line character. We do this because
  // scripts get processed prior to adding to the PDF,
  // and newlines are removed at that stage.
  const nl = String.fromCharCode(10);
  function cfgTool_emailConfig(module_name_pfx) {
    const export_url = cfgTool_getExportUrl(module_name_pfx);
    if (export_url == "") return;
    const { product_family_model_variants_map, product_ids_sku_map } =
    cfgTool_vars[module_name_pfx];

    // Collect selected product names & skus
    const sorted_selected_products =
      cfgTool_getSortedSelectedProducts(module_name_pfx);
    const seenProductIds = new Set();
    const products_msg = sorted_selected_products
      .map(({id,  model }, idx) => {
        // might be duplicates if product has multiple skus
        if (seenProductIds.has(id)) return "";
        seenProductIds.add(id);
        const sku = product_ids_sku_map[id];
        if (idx === 0) {
          const name = product_family_model_variants_map[id];
          return name + " (" + sku + ")";
        } else {
          return " + " + model + " (" + sku + ")";
        }
      })
      .join(nl);
  
    // const header_msg = 'WorkForce Enterprise Series' + nl + nl;
    //const link_msg = nl + nl + export_url;
    // const msg = header_msg + products_msg;
    const msg = products_msg;
  
    app.mailMsg({
      bUI: true,
      cTo: "",
      cSubject: "Epson WorkForce Enterprise Configuration",
      cMsg: msg,
    });
  }
  
  // Product IDs are all greater than 0
  function cfgTool_getProduct(module_name_pfx, field) {
    if (
      !cfgTool_vars[module_name_pfx] ||
      !cfgTool_vars[module_name_pfx].product_names_map ||
      !field
    ) {
      return null;
    }
  
    const product_name = cfgTool_getFieldValue(module_name_pfx, field);
    const map = cfgTool_vars[module_name_pfx].product_names_map;
    if (!map[product_name]) return null;
  
    return map[product_name];
  }
  
  function cfgTool_getFieldValue(module_name_pfx, field) {
    switch (field.type) {
      case "checkbox":
        if (field.value === "Off") return "";
        const vars = cfgTool_vars[module_name_pfx];
  
        const product_id = vars.checkbox_products[field.name];
        const product_name = vars.product_ids_name_map[product_id];
        if (!product_name) {
          console.println(
            "ERROR: product not found [" + field.name + "]: " + product_id
          );
          return "";
        }
        return product_name;
  
      default:
        return field.value;
    }
  }
  
  function cfgTool_fieldChanged(module_name_pfx, field_name) {
    var field = cfgTool_field(field_name);
    // Skip processing if current value matches previous one
    if (field.value == cfgTool_prev_vars[field_name]) {
      return;
    }

    cfgTool_prev_vars[field_name] = field.value;

    // A single selection mutates dozens of field properties and layers
    // (value/readonly/display/richValue + OCG toggles). By default Acrobat
    // redraws the page after EACH mutation; on this image-heavy page that's the
    // multi-second freeze. Doc.delay coalesces them into ONE redraw, released in
    // the finally below. Only the outermost call toggles it (a child value set
    // can re-enter this via Calculate); restore the prior value rather than
    // forcing false, in case we're nested inside another delayed batch.
    var manage_delay = cfgTool_fc_depth === 0 && !!cfgTool_doc;
    var prev_delay;
    if (manage_delay) {
      prev_delay = cfgTool_doc.delay;
      cfgTool_doc.delay = true;
    }
    cfgTool_fc_depth++;
    try {
      const { dropdown_products } = cfgTool_vars[module_name_pfx];
      const dropdownIdx = Object.keys(dropdown_products).indexOf(field_name);
      if (dropdownIdx !== -1) {
        // adjust whether child dropdowns are enabled/disabled
        cfgTool_dropdownChanged(module_name_pfx, dropdownIdx, dropdownIdx + 1);
      }
      cfgTool_reprocessLogic(module_name_pfx, field_name);
    } finally {
      cfgTool_fc_depth--;
      if (manage_delay) cfgTool_doc.delay = prev_delay;
    }
  }

  function cfgTool_disableDropdown(module_name_pfx, field_name, disabled_state) {
    const layerPrefix = module_name_pfx + "_helper_";
    const layer = layerPrefix + field_name + "_visibility";
    const field = cfgTool_field(field_name);
    field.readonly = disabled_state;
    if (!!disabled_state) hideField(field_name);
    else showField(field_name);
    toggleLayer(layer, disabled_state);
  }

  // if current dropdown options aren't the original, reset them
  function cfgTool_resetDropdownOptions(module_name_pfx, field_name) {
    const originalOptions = cfgTool_dropdown_options_state[module_name_pfx][field_name];
    const field = cfgTool_field(field_name);
    //if lengths are different, right away switch
    var updateOptions = field.numItems !== originalOptions.length;
    //otherwise compare the labels and values to be sure
    if (!updateOptions) {
      originalOptions.forEach((option, idx) => {
        const [label, value] = option;
        if (field.getItemAt(idx) !== value || field.getItemAt(idx, false) !== label) {
          updateOptions = true;
          return;
        }
      });
    }
    if (updateOptions) field.setItems(originalOptions);
  }
  
  // force disable and reset the "child" dropdown if parent changes
  // if childIndex is null use parentIndex+1
  function cfgTool_dropdownChanged(module_name_pfx, parentIndex, childIndex) {
    const { project, checkbox_products, logic_field_names_v2, same_dimensions } =
      cfgTool_vars[module_name_pfx];
    const dropdownName = module_name_pfx + "_content_dropdown-";
    const isPrimaryDropdown = parentIndex === 0;
    const parentDropdownName = dropdownName + parentIndex;
    const parentDropdown = cfgTool_field(parentDropdownName);
    if (!parentDropdown) return;
    const parentReset = parentDropdown.value === parentDropdown.getItemAt(0);
    var childDropdownName = dropdownName + childIndex;
    var childDropdown = cfgTool_field(childDropdownName);
    // reset and disable all children dropdowns
    //only condition when this shouldn't happen is when it's primary and same dimensions
    if (!isPrimaryDropdown || !same_dimensions || parentReset) {
      var i = 0;
      while (!!childDropdown) {
        childDropdown.value = childDropdown.getItemAt(0);
        cfgTool_prev_vars[childDropdownName] = childDropdown.value;
        cfgTool_disableDropdown(module_name_pfx, childDropdownName, true);
        cfgTool_resetDropdownOptions(module_name_pfx, childDropdownName);
        i += 1;
        childDropdownName = dropdownName + (childIndex + i);
        childDropdown = cfgTool_field(childDropdownName);
      }
    }
    // if the parent dropdown wasn't reset, re-enable the direct child dropdown
    if (!parentReset) {
      childDropdownName = dropdownName + childIndex;
      childDropdown = cfgTool_field(childDropdownName);
      if (!!childDropdown) {
        cfgTool_disableDropdown(module_name_pfx, childDropdownName, false);
        cfgTool_resetDropdownOptions(module_name_pfx, childDropdownName);
      }
    } 
    var match_idx = cfgTool_getImageIndexToShow(module_name_pfx);
    // if there's no match, reset options that affect dimensions
    if (match_idx < 0) {
      // try reset additional options one by one to only deal with the problematic one
      const checkboxFieldsSet = new Set(Object.keys(checkbox_products));
      logic_field_names_v2.every((field_name) => {
        const field = cfgTool_field(field_name);
        if (checkboxFieldsSet.has(field_name)) {
          if (field.type === "checkbox") {
            const oldValue = field.value;
            if (field.value !== "Off") {
              cfgTool_resetField(field, field_name);
              match_idx = cfgTool_getImageIndexToShow(module_name_pfx);
              if (match_idx > -1) return false;
              else {
                field.value = oldValue;
                cfgTool_prev_vars[field_name] = field.value;
              }
            }
          }
        }
        return true;
      });
      if (match_idx < 0) {
        //if more than one is a problem, reset all of them
        logic_field_names_v2.forEach((field_name) => {
          const field = cfgTool_field(field_name);
          if (checkboxFieldsSet.has(field_name)) {
            if (field.type === "checkbox") {
              cfgTool_resetField(field, field_name);
            }
            // TODO: INSERT WHAT TO DO IF DROPDOWN WHEN ADJUSTING WF PRO HC
            // since additional options for wf pro hc has dropdowns too
            cfgTool_prev_vars[field_name] = field.value;
          }
        })
      }
    }
  }
  
  function cfgTool_isPrimaryModelSelected(module_name_pfx) {
    var primary_name = module_name_pfx + "_content_dropdown-0";
    var field = cfgTool_field(primary_name);
    var primary_product = cfgTool_getProduct(module_name_pfx, field);
    return !!primary_product;
  }
  
  // Review provided logic, toggling layers and fields as necessary.
  // triggering_field: the field whose Calculate/MouseUp event started this chain.
  // During that event Acrobat forbids setting that field's own value, so
  // disableOptions must skip rebuilding it.
  function cfgTool_reprocessLogic(module_name_pfx, triggering_field) {
    // Hide "Selected Model" information until a "Printer Model" is Selected

    const is_primary_selected = cfgTool_isPrimaryModelSelected(module_name_pfx);
    if (!is_primary_selected) {
      cfgTool_disableAllFields(module_name_pfx, true);
      cfgTool_hideConfigSection(module_name_pfx);
      return;
    }

    // Show config section and selected options
    cfgTool_disableAllFields(module_name_pfx, false);
    cfgTool_showConfigSection(module_name_pfx);
    cfgTool_showImageFromLogic(module_name_pfx);
    cfgTool_disableOptions(module_name_pfx, triggering_field);
    var deselected = cfgTool_deselectOptions(module_name_pfx);
    // If deselection cleared a field (e.g. HCT auto-dropped when ODC is removed),
    // the field state changed after the image was painted — re-evaluate and
    // re-apply the new setting's disabled array so both the image and the
    // disabled state reflect the post-cascade selection.
    if (deselected) {
      cfgTool_showImageFromLogic(module_name_pfx);
      cfgTool_disableOptions(module_name_pfx, triggering_field);
    }
    cfgTool_updateFieldValues(module_name_pfx);
  }
  
  function cfgTool_disableAllFields(module_name_pfx, disable) {
    const { all_products } = cfgTool_vars[module_name_pfx];
    const primary_name = module_name_pfx + "_content_dropdown-0";
    Object.keys(all_products).forEach((k, idx) => {
      if (k !== primary_name) {
        const field = cfgTool_field(k);
        if (!field) return;
        if (disable) cfgTool_resetField(field, k);
        // Writing field.readonly regenerates the widget appearance in Acrobat,
        // so only write when it actually changes. This runs on every selection
        // across all fields; the redundant writes were pure overhead.
        if (field.readonly !== disable) field.readonly = disable;
      }
    });
  }

  // reset to initial values if not already the case
  function cfgTool_resetField(field, field_name) {
    // A disabled/deselected id may not correspond to a live field (e.g. a
    // bundle sub-component that isn't itself selectable). Skip gracefully so
    // reprocessLogic keeps running and the table still updates.
    if (!field) return;
    if (field.type === "checkbox" && field.value !== "Off") field.value = "Off";
    if (field.type === "combobox" && field.value !== field.getItemAt(0)) field.value = field.getItemAt(0);
    cfgTool_prev_vars[field_name] = field.value;
  }

  // deselect all options associated with deselected IDs
  // Returns true if any field was actually cleared (caller uses this to decide
  // whether to re-evaluate the image after the cascade).
  function cfgTool_deselectOptions(module_name_pfx) {
    if (!cfgTool_deselected_state[module_name_pfx]) return false;
    var changed = false;
    const { reverse_products, product_names_map } = cfgTool_vars[module_name_pfx];
    cfgTool_deselected_state[module_name_pfx].forEach((id, idx) => {
      const field_name = reverse_products[id];
      const field = cfgTool_field(field_name);
      if (!field) return;
      // reverse_products maps EVERY product of a dropdown to that one field, so a
      // deselected id only means "clear" when it is the option actually selected.
      // Otherwise deselecting a sibling (e.g. Caster Table) would wipe an
      // unrelated pick in the same dropdown (e.g. Optional Dual Cassette).
      // (Checkboxes are one field per product, so this is naturally satisfied.)
      const selected_product = cfgTool_getProduct(module_name_pfx, field);
      if (selected_product && selected_product.id === id) {
        cfgTool_resetField(field, field_name);
        changed = true;
      }
    });
    return changed;
  }
  
  // remove all options associated with disabled IDs
  // triggering_field: field whose event started this reprocess chain; Acrobat
  // forbids setting that field's own value during its Calculate/MouseUp event,
  // so we skip setItems on it (the user's selection is already correct).
  function cfgTool_disableOptions(module_name_pfx, triggering_field) {
    if (!cfgTool_disabled_state[module_name_pfx]) return;
    const { reverse_products, product_names_map, dropdown_products } = cfgTool_vars[module_name_pfx];
    // Membership map of the disabled ids (the array form stays in
    // cfgTool_disabled_state because deselect/iteration order uses it).
    const disabled_ids_map = {};
    cfgTool_disabled_state[module_name_pfx].forEach((id) => {
      disabled_ids_map[id] = true;
    });
    cfgTool_disabled_state[module_name_pfx].forEach((id, idx) => {
      const field_name = reverse_products[id];
      const field = cfgTool_field(field_name);
      // Disabled id with no live field (e.g. a non-selectable sub-component):
      // skip so the rest of reprocessLogic (and the table update) still runs.
      if (!field) return;
      if (field.type === "checkbox") {
        if (field.readonly !== true) field.readonly = true;
      } else {
        // get all options excluding the disabled ones
        const originalOptions = cfgTool_dropdown_options_state[module_name_pfx][field_name];
        if (!originalOptions) return;
        const newOptions = [originalOptions[0]];
        originalOptions.slice(1).forEach((option, idx) => {
          const [label, value] = option;
          const product_entry = product_names_map[value];
          if (!product_entry) return;
          const productId = product_entry.id;
          if (!disabled_ids_map[productId]) {
            newOptions.push([label, value]);
          }
        });
        var updateOptions = newOptions.length !== field.numItems;
        if (!updateOptions) {
          newOptions.forEach((opt, idx) => {
            const [label, value] = opt;
            if (label != field.getItemAt(idx, false) || value != field.getItemAt(idx)) {
              updateOptions = true;
            }
          });
        }
        if (updateOptions) {
          // If this IS the field that triggered the event, Acrobat forbids
          // setting its value during its own Calculate — skip setItems entirely.
          // The user's selection is already correct; the trimmed option list will
          // be applied the next time a different field triggers reprocessLogic.
          if (field_name === triggering_field) return;
          // For non-triggering fields: only call setItems when the current
          // selection is itself disabled (e.g. the user previously picked
          // an option that the new matched setting disables). When the current
          // pick is still valid, trimming the sibling options via setItems
          // would reset the value to the placeholder, triggering a cascade
          // Calculate chain that fires cfgTool_dropdownChanged for this field
          // and can corrupt prev_vars of unrelated fields (checkboxes).
          // Defer the trim — it applies on the next reprocessLogic triggered
          // by a different field.
          var currentProduct = cfgTool_getProduct(module_name_pfx, field);
          // Skip setItems only when the current pick is a valid (non-disabled) product.
          // When nothing is selected (placeholder) setItems is safe — it keeps the
          // value at placeholder so no Calculate cascade fires.
          // When the current pick IS disabled, setItems is needed to reset it.
          var currentPickIsValid =
            !!currentProduct && !disabled_ids_map[currentProduct.id];
          if (currentPickIsValid) return;
          // Current pick is being disabled: rebuild options (value resets to
          // placeholder) so the dropdown clearly shows no valid selection.
          field.setItems(newOptions);
        }
        // if the only option left is the first one, just disable and hide the dropdown
        const isDropdown = !!dropdown_products[field_name];
        if (newOptions.length === 1 && isDropdown) {
          cfgTool_disableDropdown(module_name_pfx, field_name, true);
        }
      }
    });
  }
  
  function cfgTool_getExportUrl(module_name_pfx) {
    // Require primary product to be selected
    const is_primary_selected = cfgTool_isPrimaryModelSelected(module_name_pfx);
    if (!is_primary_selected) return "";
  
    const dropdowns = cfgTool_enumerateDropdowns(module_name_pfx);
    const checkboxes = cfgTool_enumerateCheckboxes(module_name_pfx);
  
    var query_key_values = [];
    dropdowns.forEach((d, idx) => {
      var selected_product = cfgTool_getProduct(module_name_pfx, d);
      if (!selected_product) return;
      const { id } = selected_product;
      query_key_values.push("d" + idx + "=" + id);
    });
    var selected_checkbox_product_ids = [];
    checkboxes.forEach((c, idx) => {
      var selected_product = cfgTool_getProduct(module_name_pfx, c);
      if (!selected_product) return;
      const { id } = selected_product;
      selected_checkbox_product_ids.push(id);
    });
  
    if (selected_checkbox_product_ids.length > 0) {
      // Separator is "." (not ",") on purpose: URLSearchParams.toString() — used
      // by both the landing page and the on-screen export to re-serialize this
      // query before it becomes the generated PDF's S3 key — percent-encodes a
      // comma to %2C, baking a literal "%2C" into the object key that the
      // single-decode download URL can never match. "." survives toString()
      // untouched, so the key and the download link stay byte-identical.
      query_key_values.push("c0=" + selected_checkbox_product_ids.join("."));
    }
  
    const { api_url, project, s3_key } = globals;
    const base_target_url = api_url + "/spe/" + project + "/" + s3_key + "/k?";
    const query_string = query_key_values.join("&");
    const export_url = base_target_url + "cache_key=" + escape(query_string);
    return export_url;
  }
  
  function cfgTool_export(module_name_pfx) {
    const export_url = cfgTool_getExportUrl(module_name_pfx);
    if (export_url !== "") app.launchURL(export_url);
  }
  
  // Initial configuration - attach on_change event to existing levers
  function cfgTool_init() {
    // Batch the load-time field/layer churn into one redraw (same reason as
    // cfgTool_fieldChanged). cfgTool_dropdownChanged below sets dropdown values,
    // which can re-enter cfgTool_fieldChanged via Calculate; the depth guard
    // there leaves the delay batch alone while we hold it here.
    var manage_delay = !!cfgTool_doc;
    var prev_delay;
    if (manage_delay) {
      prev_delay = cfgTool_doc.delay;
      cfgTool_doc.delay = true;
    }
    cfgTool_fc_depth++;
    try {
      var module_names = Object.keys(cfgTool_vars);
      module_names.forEach((module_name) => {
        cfgTool_dropdown_options_state[module_name] = [];
        cfgTool_disabled_state[module_name] = [];
        cfgTool_deselected_state[module_name] = [];
        cfgTool_match_idx[module_name] = -1;
        // Sentinel: never equals a real layer index (>= -1), so the first
        // showImageFromLogic always applies regardless of the PDF's initial state.
        cfgTool_shown_image_layer[module_name] = -2;
        cfgTool_shown_dims_layer[module_name] = -2;

        cfgTool_initCheckboxListeners(module_name);
        cfgTool_initDropdownListeners(module_name);

        // Initialize
        const { dropdown_products } = cfgTool_vars[module_name];
        for (var i = 0; i < Object.keys(dropdown_products).length - 1; i++) {
          cfgTool_dropdownChanged(module_name, i, i + 1);
        }
        // Reprocess logic
        cfgTool_reprocessLogic(module_name);
      });
    } finally {
      cfgTool_fc_depth--;
      if (manage_delay) cfgTool_doc.delay = prev_delay;
    }
  }
  cfgTool_init();
} catch (e) {
  console.println('ERROR - Config tool: ' + e);
}
`;

export default scripts;
